/**
 * 外部SA (admins: dept='sales' && level='external') の一覧取得とオンボーディング操作。
 *
 * - 一覧読取: admins コレクション (owner のみ read 可) を SA で絞り込み。
 * - 更新: updateSaOnboarding / sendSaMail Callable 経由 (admins write は owner 限定のため)。
 *
 * 仕様: docs/01_spec/spec_sa_management.md / project_sa_management_screen
 */
import {
  computeSaStatus,
  type Admin,
  type SaOnboardingStatus,
} from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';


import { db, functions } from '@/lib/firebase';

export interface SaAgentRecord extends Admin {
  /** Firestore document ID (= uid)。 */
  uid: string;
  /** 導出ステータス (saOnboarding.status があればそれ、無ければフラグから算出)。 */
  saStatus: SaOnboardingStatus;
}

/**
 * @param options.enabled admins コレクションは owner のみ read 可。
 *   owner 以外の画面から呼ぶ場合は `enabled: false` を渡して権限エラーを避ける。
 */
export function useSaAgents(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['sa_agents'],
    enabled: options?.enabled ?? true,
    queryFn: async (): Promise<SaAgentRecord[]> => {
      const snap = await getDocs(
        query(collection(db, 'admins'), orderBy('createdAt', 'asc')),
      );
      return snap.docs
        .map((d) => ({ uid: d.id, ...(d.data() as Admin) }))
        .filter((a) => a.dept === 'sales' && a.level === 'external')
        .map((a) => ({
          ...a,
          saStatus: a.saOnboarding?.status ?? computeSaStatus(a.saOnboarding ?? {}),
        }));
    },
  });
}

export interface UpdateSaOnboardingInput {
  uid: string;
  patch?: {
    consentConfirmed?: boolean;
    paymentConfirmed?: boolean;
    briefingAttended?: boolean;
  };
  /** 契約開始日の手動上書き 'YYYY-MM-DD'。 */
  contractStartDate?: string | null;
  /** 契約年数 (1 以上)。 */
  contractYears?: number;
}

interface UpdateSaOnboardingResult {
  ok: true;
  status: SaOnboardingStatus;
  contractStartDate: string | null;
  contractEndDate: string | null;
}

const updateSaOnboardingFn = httpsCallable<
  UpdateSaOnboardingInput,
  UpdateSaOnboardingResult
>(functions, 'updateSaOnboarding');

export function useUpdateSaOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateSaOnboardingInput) =>
      (await updateSaOnboardingFn(input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sa_agents'] }),
  });
}

export interface SendSaMailInput {
  uid: string;
  subject: string;
  body: string;
}

const sendSaMailFn = httpsCallable<
  SendSaMailInput,
  { ok: true; messageId: string; to: string }
>(functions, 'sendSaMail');

export function useSendSaMail() {
  return useMutation({
    mutationFn: async (input: SendSaMailInput) =>
      (await sendSaMailFn(input)).data,
  });
}

interface RenewResult {
  ok: true;
  contractTerm: number;
  contractStartDate: string;
  contractEndDate: string;
}

const renewSaContractFn = httpsCallable<{ uid: string }, RenewResult>(
  functions,
  'renewSaContract',
);

/** 管理者が SA 契約を次期へ更新する (SA管理画面の「更新」ボタン)。 */
export function useRenewSaContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (uid: string) => (await renewSaContractFn({ uid })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sa_agents'] }),
  });
}
